import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchSpacesForUser } from '../../../api/api_users';
import { updateHubName, addHubToSpace } from '../../../api/api_hubs';
import { createEmptySpace } from '../../../api/api_spaces';

const ConfigureHubFast = () => {
    const { translations } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const { hub } = location.state  || {};

    const [spaces, setSpaces] = useState([]);
    const [hubName, setHubName] = useState( '');
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [newSpaceName, setNewSpaceName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const loadSpaces = async () => {
            setLoading(true);
            try {
                const fetchedSpaces = await fetchSpacesForUser();
                setSpaces(Array.isArray(fetchedSpaces.data) ? fetchedSpaces.data : []);
            } catch (err) {
                setError(translations.error_fetching_spaces || 'Failed to fetch spaces.');
                setSpaces([]);
            } finally {
                setLoading(false);
            }
        };
        loadSpaces();
    }, [translations.error_fetching_spaces]);

    const handleBack = () => navigate(-1);

    // When selecting an existing space, clear new space input
    const handleSelectSpace = (spaceId) => {
        setSelectedSpace(spaceId);
        setNewSpaceName('');
    };

    // When typing a new space name, clear existing selection
    const handleNewSpaceChange = (e) => {
        setNewSpaceName(e.target.value);
        setSelectedSpace(null);
    };

    const handleHubNameChange = (e) => {
        setHubName(e.target.value);
        setError('');
        setSuccess('');
    };

    const handleUpdateHub = async () => {
        setError('');
        setSuccess('');
        if (!hubName.trim()) {
            setError(translations.error_hub_name_required || 'El nombre del hub es obligatorio.');
            return;
        }
        if (!selectedSpace && !newSpaceName.trim()) {
            setError(translations.error_select_space || 'Selecciona o crea un espacio.');
            return;
        }
        const token = localStorage.getItem('userToken');
        setLoading(true);
        try {
            await updateHubName(hub.id, hubName, token);
            let spaceId = selectedSpace;
            if (newSpaceName.trim()) {
                const newSpace = await createEmptySpace(newSpaceName.trim(), token);
                spaceId = newSpace.id;
            }
            await addHubToSpace(hub.id, spaceId, token);
            setSuccess(translations.success_hub_configured || 'Hub configurado correctamente.');
            setTimeout(() => navigate('/home'), 1000);
        } catch (err) {
            setError(translations.error_saving || 'Error actualizando el hub o añadiendo el espacio.');
        } finally {
            setLoading(false);
        }
    };

    const isSaveDisabled = loading || !hubName.trim() || (!selectedSpace && !newSpaceName.trim());

    return (
        <div className="sm-page">
            <div className="sm-container">
                <div className="header">
                    <button onClick={handleBack} className="back-button" aria-label={translations.go_back}>
                        &#8592;
                    </button>
                    <h1>{translations.new_device}</h1>
                </div>

                <div className="content">

                    {/* Indication for hub name input */}
                    <div className="input-section">
                        <label className="label" htmlFor="hub-name-input">
                            Cambia el nombre del Hub:
                        </label>
                        <small className="hint-text">
                            El nombre que escribas aquí será el nombre del hub en la app.
                        </small>
                        <input
                            id="hub-name-input"
                            type="text"
                            placeholder={translations.enter_hub_name}
                            value={hubName}
                            onChange={handleHubNameChange}
                            className="hub-name-input"
                            maxLength={40}
                            autoFocus
                        />
                    </div>

                    {/* Select space section */}
                    <div className="spaces-section input-section">
                        <label className="label">
                            Asigna el hub a un espacio existente:
                        </label>
                        <small className="hint-text">
                            Pulsa sobre el nombre de un espacio para seleccionarlo.
                        </small>
                        <div className="spaces-list">
                            {spaces.length === 0 && !loading && (
                                <div className="no-spaces">{translations.no_spaces_found}</div>
                            )}
                            {spaces.map((space) => (
                                <div
                                    key={space.id}
                                    tabIndex={0}
                                    className={`space-item ${selectedSpace === space.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectSpace(space.id)}
                                    onKeyDown={e => e.key === 'Enter' && handleSelectSpace(space.id)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '8px 12px',
                                        margin: '6px 0',
                                        borderRadius: '8px',
                                        border: selectedSpace === space.id ? '2px solid #007bff' : '1px solid #ccc',
                                        background: selectedSpace === space.id ? '#e6f0ff' : '#f9f9f9',
                                        fontWeight: selectedSpace === space.id ? 600 : 400
                                    }}
                                >
                                    {space.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Create new space section */}
                    <div className="new-space-section input-section">
                        <label className="label" htmlFor="new-space-input">
                            O crea un nuevo espacio:
                        </label>
                        <small className="hint-text">
                            Si introduces un nombre aquí, se creará un nuevo espacio y el hub se asignará a él automáticamente.
                        </small>
                        <input
                            id="new-space-input"
                            type="text"
                            placeholder={translations.new_space_name}
                            value={newSpaceName}
                            onChange={handleNewSpaceChange}
                            className="new-space-input"
                            maxLength={40}
                        />
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <button
                        onClick={handleUpdateHub}
                        className="general-button save-button"
                        disabled={isSaveDisabled}
                    >
                        {loading ? translations.saving : translations.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfigureHubFast;
